import { countLabel } from 'utils/ru';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorageIcon from '@mui/icons-material/Storage';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useCreateWarehouse, useWarehouseQuota } from 'api/warehouse';
import { extractApiError, hasFieldErrors, normalizeFieldErrors } from 'api/errors';
import useRole from 'hooks/useRole';

import { useTranslation } from 'react-i18next';

// The backend's engines for a warehouse of your own. S3 (Iceberg) is left out:
// the Lakehouse is the platform's, and a bring-your-own Iceberg catalog needs
// settings this form does not collect.
const ENGINES = [
  { value: 'clickhouse', label: 'ClickHouse', defaultPort: 8123 },
  { value: 'postgres', label: 'PostgreSQL', defaultPort: 5432 },
  { value: 'trino', label: 'Trino', defaultPort: 8080 },
  { value: 'snowflake', label: 'Snowflake', defaultPort: 443 },
  { value: 'bigquery', label: 'BigQuery', defaultPort: 443 },
  { value: 'redshift', label: 'Redshift', defaultPort: 5439 },
  { value: 'duckdb', label: 'DuckDB', defaultPort: 0 },
];

export default function NewWarehouseServicePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const createWarehouse = useCreateWarehouse();
  const { data: quota, isLoading: quotaLoading, error: quotaError } = useWarehouseQuota();
  const atLimit = quota && !quota.can_create;

  const [form, setForm] = useState({
    name: '',
    engine: 'clickhouse',
    host: '',
    port: 8123,
    database: '',
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleEngineChange = (engine) => {
    const preset = ENGINES.find((e) => e.value === engine);
    set('engine', engine);
    if (preset) set('port', preset.defaultPort);
  };

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = t('data.nameIsRequired');
    if (!form.host.trim()) errors.host = t('data.hostIsRequired');
    if (!form.database.trim()) errors.database = t('data.databaseIsRequired');
    return errors;
  };

  const handleSubmit = async () => {
    if (!isAdmin || !quota?.can_create || createWarehouse.isPending) return;
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    setSubmitError(null);
    try {
      const warehouse = await createWarehouse.mutateAsync({
        name: form.name.trim(),
        engine: form.engine,
        host: form.host.trim(),
        port: Number(form.port),
        database: form.database.trim(),
        username: form.username.trim(),
        password: form.password,
      });
      navigate(`/console/pipelines/storage/${warehouse.public_id}`);
    } catch (err) {
      if (hasFieldErrors(err)) {
        setFormErrors(normalizeFieldErrors(err.response.data));
      } else {
        setSubmitError(extractApiError(err, t('data.failedToCreateWarehousePleaseTryAgain')));
      }
    }
  };

  if (!isAdmin) return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>{t('data.addWarehouse')}</Typography>
      <Alert severity="info">{t('data.workspaceData.adminOnly')}</Alert>
      <Button sx={{ mt: 2 }} onClick={() => navigate('/console/pipelines?tab=storage')}>{t('data.back')}</Button>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/console/pipelines?tab=storage')} size="small" color="inherit">{t('data.back')}</Button>
        <Divider orientation="vertical" flexItem />
        <StorageIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>{t('data.addWarehouse')}</Typography>
      </Box>

      <Box sx={{ maxWidth: 520 }}>
        {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
        {quotaError && <Alert severity="warning" sx={{ mb: 2 }}>{t('data.workspaceData.quotaUnavailable')}</Alert>}

        {atLimit && (
          <Alert severity="warning" sx={{ mb: 2 }}>{t('data.youVeReachedYourPlanSLimitOf')}{' '}{countLabel(quota.limit, 'warehouse')}{' '}{t('data.ofYourOwn')}{' '}
            <Button size="small" onClick={() => navigate('/console/pipelines?tab=storage')}>{t('data.manageWarehouses')}</Button>{' '}{t('data.toDisableOneOrUpgradeYourPlanToAddMore')}</Alert>
        )}

        <TextField label={t('data.name')} value={form.name} onChange={(e) => set('name', e.target.value)} required fullWidth sx={{ mb: 2 }} error={!!formErrors.name} helperText={formErrors.name || t('data.friendlyNameForThisWarehouse')} />

        <TextField select label={t('data.engine')} value={form.engine} onChange={(e) => handleEngineChange(e.target.value)} fullWidth sx={{ mb: 2 }}>
          {ENGINES.map((e) => <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>)}
        </TextField>

        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>{t('data.connection')}</Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField label={t('data.host')} value={form.host} onChange={(e) => set('host', e.target.value)} required fullWidth error={!!formErrors.host} helperText={formErrors.host} />
          <TextField label={t('data.port')} type="number" value={form.port} onChange={(e) => set('port', e.target.value)} sx={{ width: 120 }} />
        </Box>

        <TextField label={t('data.database')} value={form.database} onChange={(e) => set('database', e.target.value)} required fullWidth sx={{ mb: 2 }} error={!!formErrors.database} helperText={formErrors.database} />
        <TextField label={t('data.username')} value={form.username} onChange={(e) => set('username', e.target.value)} fullWidth sx={{ mb: 2 }} />
        <TextField
          label={t('data.password')}
          type={showPassword ? 'text' : 'password'}
          value={form.password}
          onChange={(e) => set('password', e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPassword((s) => !s)} edge="end">
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button variant="contained" onClick={handleSubmit} disabled={createWarehouse.isPending || quotaLoading || !!quotaError || !quota?.can_create} startIcon={createWarehouse.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
          {createWarehouse.isPending ? t('data.creating') : t('data.createWarehouse')}
        </Button>
      </Box>
    </Box>
  );
}
